import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { filteringContentTypes } from '@app/library/library-filter-widget/library-filter-widget.constants';
import { ContentService, PlatformService, ScreenService } from '@core/services';
import { IContentBlock } from '@core/services/interfaces';
import { ChangableComponent } from '@models/changable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  ContentTypeEnum,
  CollectionItem,
  IFindContentFilter,
  IFindLessonsFilter,
  IPagedResults,
  Content,
  ContentDetailsType,
  IUserContentStats,
  PublishStatusEnum,
  FindContentStrategyEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { catchError, debounceTime, filter, mergeAll, switchMap, takeUntil, tap } from 'rxjs/operators';

type ContentBlockType = IContentBlock<Content, IFindContentFilter>;
type ButtonType = 'default' | 'added' | 'alreadyAdded';

const contentDetails: ContentDetailsType[] = [
  'id',
  'type',
  'title',
  'sub_title',
  'cover:md',
  'author:sm',
  'subject',
  'language',
  'published_at',
  'archived_at',
  'privacy',
];

@Component({
  selector: 'app-content-selector',
  templateUrl: './content-selector.component.html',
  styleUrls: ['./content-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentSelectorComponent extends ChangableComponent implements OnInit {
  @Input()
  visible: boolean;

  @Input()
  selectedItems: CollectionItem[];

  @Input()
  selectedLessons: Content[];

  @Input()
  set author_id(author_id: string) {
    this.items.filter.author_id = author_id;
    this.filter$.next(this.items.filter);
  }

  @Output()
  closed = new EventEmitter<boolean>();

  @Output()
  selected = new EventEmitter<Content[]>();

  public contentTypesOptions: Array<{ id: ContentTypeEnum; title: string }> = [];
  public contentType: ContentTypeEnum;
  public term = '';
  public items: ContentBlockType = {
    items: [],
    filter: {
      publish_status: [PublishStatusEnum.published, PublishStatusEnum.private],
    },
    loading: false,
    loaded: false,
    pagination: {
      page: 1,
      pageSize: 100,
      total: null,
      totalPages: null,
    },
  };
  public isPortrait: boolean;
  public noContent: boolean;
  public isInvalid: boolean;

  // Фильтр для поиска уроков
  protected filter$: BehaviorSubject<Partial<IFindLessonsFilter>> = new BehaviorSubject(null);
  public selectedIds: string[] = [];
  private contentStats: IUserContentStats;
  public item: Partial<Content> = null;
  public showModal = false;
  public btnState: ButtonType = 'default';

  constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private contentService: ContentService,
    private screenService: ScreenService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  ngOnInit() {
    this.filter$.next(this.items.filter);
    of(this.watchScreenOptions$, this.watchFilter$)
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });

    this.contentTypesOptions = filteringContentTypes.map((contentType) => ({
      id: contentType,
      title: `content-types-title.${ContentTypeEnum[contentType]}`,
    }));
  }

  protected get watchScreenOptions$() {
    return this.screenService.options.pipe(
      tap((options) => {
        this.isPortrait = options.isPortrait;
        this.markForCheck();
      }),
    );
  }

  get watchFilter$() {
    return this.filter$.pipe(debounceTime(300)).pipe(
      filter((filters) => !!filters),
      switchMap((filters) => {
        this.items.loading = true;
        if (this.isAddLessonsFromContent) {
          filters.use = FindContentStrategyEnum.library;
          delete filters.publish_status;
        }
        return this.contentService.getContent(filters, this.items.pagination, false, contentDetails);
      }),
      tap((data: IPagedResults<Content[]>) => {
        this.items.items = data.results;
        this.items.pagination = data.pagination;
        this.items.loading = false;
        this.items.loaded = true;
        this.markForCheck();

        if (data.pagination.total === 0) {
          // если ничего не нашлось, то возможно ничего вообще и нет
          this.loadContentStats();
        }
      }),
      catchError((err: any) => {
        this.markForCheck();
        return throwError(err);
      }),
    );
  }

  public updateFilter() {
    this.filter$.next(this.items.filter);
  }

  public updateFilterTerm() {
    if ((this.term || '').length >= 3) {
      this.items.filter.term = this.term;
    } else {
      delete this.items.filter.term;
    }
    this.filter$.next(this.items.filter);
  }

  public isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  public get isAddLessonsFromContent(): boolean {
    return !!this.selectedLessons;
  }

  public isLessonOnCollection(id: string): boolean {
    if (this.selectedLessons) {
      return !!this.selectedLessons.find((item) => item.id === id);
    }
    return !!this.selectedItems?.find((item) => item.content_id === id);
  }

  public selectItem(item: CollectionItem) {
    this.selectedIds = [item.id];
    this.onOpenCard(item);
    this.detectChanges();
  }

  public saveSelection() {
    this.isInvalid = false;
    if (!this.isLessonOnCollection(this.selectedIds[0])) {
      const selected = this.items.items.filter((item) => this.selectedIds.includes(item.id));
      if (selected.length) {
        this.btnState = 'added';
        this.onCloseCard();
        this.selected.emit(selected);
        this.setTimeout(() => {
          this.btnState = 'default';
          this.selectedIds = [];
          this.detectChanges();
        }, 1000);
      } else {
        this.isInvalid = true;
      }
    } else {
      this.btnState = 'alreadyAdded';
      this.onCloseCard();
      this.setTimeout(() => {
        this.btnState = 'default';
        this.selectedIds = [];
        this.detectChanges();
      }, 1000);
    }
  }

  public selectContentType(type: ContentTypeEnum) {
    this.contentType = type;
    if (!type) {
      delete this.items.filter.content_type;
    } else {
      this.items.filter.content_type = type;
    }
    this.updateFilter();
  }

  public trackContentById(index: number, item: Content) {
    return item.id;
  }

  /**
   * Загрузить статистику и показать блок "Создайте урок", если у пользователя ещё нет созданных уроков
   *
   * @protected
   */
  protected loadContentStats() {
    if (this.contentStats !== null && this.contentStats !== undefined) {
      return;
    }

    // NOTE (!) так же можно проверять не статистику, а сегменты и ачивки пользователя
    // @see UserAchievementEnum.created_content_once
    // @see UserSegmentEnum.has_content

    this.contentService
      .getUserStats(this.items.filter.author_id as any)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (stats) => {
          this.contentStats = stats;
          if (stats.total === 0) {
            this.noContent = true;
            this.markForCheck();
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onOpenCard(item: CollectionItem) {
    this.item = item;
    this.showModal = true;
  }

  public onCloseCard() {
    this.item = null;
    this.showModal = false;
  }

  public trackByFn(index) {
    return index;
  }
}
